// Parse KML / GeoJSON / zipped shapefile in the browser into a temporary (session-only) map layer.
const PALETTE = ["#2563EB", "#DB2777", "#F59E0B", "#7C3AED", "#0891B2", "#16A34A"];
let counter = 0;

function kmlToGeoJSON(text) {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("Invalid KML");
  const coords = (el) => el.textContent.trim().split(/\s+/).map((t) => t.split(",").map(Number).slice(0, 2)).filter((p) => p.length === 2 && p.every(Number.isFinite));
  const features = [];
  doc.querySelectorAll("Placemark").forEach((pm) => {
    const props = {};
    const name = pm.querySelector(":scope > name")?.textContent?.trim(); if (name) props.name = name;
    const desc = pm.querySelector(":scope > description")?.textContent?.trim(); if (desc) props.description = desc;
    pm.querySelectorAll("ExtendedData Data").forEach((d) => { props[d.getAttribute("name")] = d.querySelector("value")?.textContent?.trim() ?? ""; });
    pm.querySelectorAll("ExtendedData SimpleData").forEach((d) => { props[d.getAttribute("name")] = d.textContent.trim(); });
    const polys = [...pm.querySelectorAll("Polygon")].map((pg) => {
      const outer = pg.querySelector("outerBoundaryIs coordinates"); if (!outer) return null;
      const rings = [coords(outer), ...[...pg.querySelectorAll("innerBoundaryIs coordinates")].map(coords)];
      return rings;
    }).filter(Boolean);
    const lines = [...pm.querySelectorAll("LineString > coordinates")].map(coords);
    const points = [...pm.querySelectorAll("Point > coordinates")].map(coords).map((c) => c[0]).filter(Boolean);
    let geometry = null;
    if (polys.length === 1) geometry = { type: "Polygon", coordinates: polys[0] };
    else if (polys.length > 1) geometry = { type: "MultiPolygon", coordinates: polys };
    else if (lines.length === 1) geometry = { type: "LineString", coordinates: lines[0] };
    else if (lines.length > 1) geometry = { type: "MultiLineString", coordinates: lines };
    else if (points.length === 1) geometry = { type: "Point", coordinates: points[0] };
    else if (points.length > 1) geometry = { type: "MultiPoint", coordinates: points };
    if (geometry) features.push({ type: "Feature", properties: props, geometry });
  });
  return { type: "FeatureCollection", features };
}

function normalize(gj) {
  if (gj.type === "FeatureCollection") return gj;
  if (gj.type === "Feature") return { type: "FeatureCollection", features: [gj] };
  return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: gj }] };
}

function boundsOf(gj) {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  const walk = (c) => { if (typeof c[0] === "number") { minx = Math.min(minx, c[0]); maxx = Math.max(maxx, c[0]); miny = Math.min(miny, c[1]); maxy = Math.max(maxy, c[1]); } else c.forEach(walk); };
  gj.features.forEach((f) => f.geometry && walk(f.geometry.coordinates));
  return Number.isFinite(minx) ? [[miny, minx], [maxy, maxx]] : null;
}

export async function parseLayerFile(file) {
  const ext = file.name.split(".").pop().toLowerCase();
  let gj;
  if (ext === "kml") gj = kmlToGeoJSON(await file.text());
  else if (ext === "geojson" || ext === "json") gj = normalize(JSON.parse(await file.text()));
  else if (ext === "zip" || ext === "kmz") {
    const { default: shp } = await import("shpjs");
    const buf = await file.arrayBuffer();
    if (ext === "kmz") {
      const { iter } = await import("but-unzip");
      const entry = [...iter(new Uint8Array(buf))].find((e) => e.name.toLowerCase().endsWith(".kml"));
      if (!entry) throw new Error("KMZ has no .kml inside");
      gj = kmlToGeoJSON(new TextDecoder().decode(await entry.read()));
    } else {
      const out = await shp(buf);
      gj = Array.isArray(out) ? { type: "FeatureCollection", features: out.flatMap((o) => o.features) } : normalize(out);
    }
  } else throw new Error("Use .kml, .kmz, .geojson/.json or a zipped shapefile");
  gj.features = gj.features.filter((f) => f.geometry);
  if (!gj.features.length) throw new Error("No features found in the file");
  const bounds = boundsOf(gj);
  const id = `tmp-${Date.now()}-${counter++}`;
  return {
    id, temp: true, kind: "vector", source_format: ext, name: file.name.replace(/\.[^.]+$/, "").slice(0, 60), original_filename: file.name, size_bytes: file.size,
    color: PALETTE[counter % PALETTE.length], opacity: 0.45, visible: true, geojson: gj, bounds, feature_count: gj.features.length,
  };
}
